import React from 'react';
import { Package, MapPin, CreditCard } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigation } from '../context/NavigationContext';
import { useApp } from '../context/AppContext';
import { Screen } from '../types';
import { Header } from '../components/ui/Header';
import { Button } from '../components/ui/Button';
export function OrderDetailScreen() {
  const {
    currentParams,
    navigate
  } = useNavigation();
  const {
    orders
  } = useApp();
  const orderId = currentParams?.orderId;
  const order = orders.find(o => o.id === orderId) || orders[0];
  if (!order) return null;
  const steps = ['Processing', 'Shipped', 'Delivered'];
  const currentStepIndex = steps.indexOf(order.status);
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} className="min-h-screen bg-gray-50 pb-8" data-id="element-864">
      <Header title="Order Details" data-id="element-865" />

      <div className="p-4 space-y-4" data-id="element-866">
        {/* Status Header */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-50" data-id="element-867">
          <div className="flex justify-between items-center mb-4" data-id="element-868">
            <div data-id="element-869">
              <p className="text-sm text-gray-500" data-id="element-870">Order ID</p>
              <p className="font-bold text-gray-900" data-id="element-871">{order.id}</p>
            </div>
            <div className="text-right" data-id="element-872">
              <p className="text-sm text-gray-500" data-id="element-873">Date</p>
              <p className="font-medium text-gray-900" data-id="element-874">
                {new Date(order.date).toLocaleDateString()}
              </p>
            </div>
          </div>

          {/* Timeline */}
          <div className="relative pt-4 pb-2" data-id="element-875">
            <div className="absolute top-6 left-6 right-6 h-1 bg-gray-100 rounded-full" data-id="element-876">
              <div className="h-full bg-primary rounded-full transition-all" style={{
              width: `${Math.max(0, currentStepIndex) / (steps.length - 1) * 100}%`
            }} data-id="element-877"></div>
            </div>
            <div className="flex justify-between relative z-10" data-id="element-878">
              {steps.map((step, idx) => <div key={step} className="flex flex-col items-center gap-2" data-id="element-879">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${idx <= currentStepIndex ? 'bg-primary border-primary text-white' : 'bg-white border-gray-200 text-transparent'}`} data-id="element-880">
                    ✓
                  </div>
                  <span className={`text-xs font-medium ${idx <= currentStepIndex ? 'text-primary' : 'text-gray-400'}`} data-id="element-881">
                    {step}
                  </span>
                </div>)}
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-50" data-id="element-882">
          <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2" data-id="element-883">
            <Package className="w-5 h-5 text-primary" data-id="element-884" /> Items (
            {order.items.length})
          </h3>
          <div className="space-y-3" data-id="element-885">
            {order.items.map((item, idx) => <div key={idx} className="flex gap-3 pb-3 border-b border-gray-50 last:border-0 last:pb-0" data-id="element-886">
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-100 shrink-0" data-id="element-887">
                  <img src={item.product.image} alt="" className="w-full h-full object-cover" data-id="element-888" />
                </div>
                <div className="flex-1" data-id="element-889">
                  <h4 className="text-sm font-medium text-gray-900 line-clamp-1" data-id="element-890">
                    {item.product.name}
                  </h4>
                  <p className="text-xs text-gray-500 mb-1" data-id="element-891">
                    Qty: {item.quantity}
                  </p>
                  <p className="text-sm font-bold text-primary" data-id="element-892">
                    ₦{(item.product.price * item.quantity).toLocaleString()}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* Shipping & Payment */}
        <div className="grid grid-cols-2 gap-4" data-id="element-893">
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-50" data-id="element-894">
            <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-1 text-sm" data-id="element-895">
              <MapPin className="w-4 h-4 text-primary" data-id="element-896" /> Shipping
            </h3>
            <p className="text-xs text-gray-600 leading-relaxed" data-id="element-897">
              {order.shippingAddress}
            </p>
          </div>
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-50" data-id="element-898">
            <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-1 text-sm" data-id="element-899">
              <CreditCard className="w-4 h-4 text-primary" data-id="element-900" /> Payment
            </h3>
            <p className="text-xs text-gray-600" data-id="element-901">
              Credit Card
              <br data-id="element-902" />
              •••• 4242
            </p>
          </div>
        </div>

        {/* Total */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-50" data-id="element-903">
          <div className="flex justify-between items-center text-lg font-bold text-gray-900" data-id="element-904">
            <span data-id="element-905">Total Paid</span>
            <span className="text-primary" data-id="element-906">
              ₦{order.total.toLocaleString()}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="pt-4 space-y-3" data-id="element-907">
          {['Processing', 'Shipped'].includes(order.status) && <Button fullWidth onClick={() => navigate(Screen.ORDER_TRACKING, {
          orderId: order.id
        })} data-id="element-908">
              Track Order
            </Button>}

          {order.status === 'Delivered' && <>
              <Button fullWidth onClick={() => navigate(Screen.WRITE_REVIEW, {
            orderId: order.id
          })} data-id="element-909">
                Write a Review
              </Button>
              <Button fullWidth variant="outline" onClick={() => navigate(Screen.RETURN_REQUEST, {
            orderId: order.id
          })} data-id="element-910">
                Request Return
              </Button>
            </>}
        </div>
      </div>
    </motion.div>;
}